import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CountryUtils, ExtendedProblemDetail, PhoneNumberUtils } from '@buspro/shared/common';
import { RegisterTenantDto as RegisterNewTenantDto } from '@buspro/web/apis';
import { CompaniesApi } from '@buspro/web/apis';
import { ApiError, ErrorDisplayComponent, useFormErrorHandler } from '@buspro/web/core';
import { FeFormMessagesService, FormMessageComponent, InputErrorDisplayComponent } from '@fulleffect/ngx-tailings';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'buspro-admin-register-tenant-modal',
  standalone: true,
  imports: [
    CommonModule,
    NzModalModule,
    NzFormModule,
    ReactiveFormsModule,
    NzInputModule,
    NzSelectModule,
    NzDividerComponent,
    FormMessageComponent,
    InputErrorDisplayComponent,
    NzSpinModule,
    DigitOnlyModule,
    ErrorDisplayComponent,
  ],
  template: `
    <nz-modal
      nzWidth="600px"
      (nzOnOk)="submitForm()"
      (nzOnCancel)="cancelled.emit($event)"
      [nzVisible]="isVisible"
      [nzOkDisabled]="form.invalid || isSubmitting"
      [nzCancelDisabled]="isSubmitting"
      [nzOkText]="isSubmitting ? 'Registering...' : 'Register'"
      [nzOkLoading]="isSubmitting"
      nzTitle="Register New Business"
    >
      <ng-container *nzModalContent>
        <nz-spin [nzSpinning]="mutation.isPending()">
          <!-- Error Display Component -->
      <div class="mb-4">
        <app-error-display
          *ngIf="showError()"
          [error]="errorDetails"
          [showRetry]="true"
        >
        </app-error-display>
      </div>

          <fe-form-message title="Ops! Something went wrong"></fe-form-message>
          <form
            nz-form
            class="mt-6"
            [formGroup]="form"
            (ngSubmit)="submitForm()"
          >
            <nz-form-item>
              <nz-form-label
                [nzSm]="8"
                [nzXs]="24"
                nzFor="companyName"
                [nzRequired]="true"
              >
                <span>Company Name</span>
              </nz-form-label>
              <nz-form-control [nzSm]="14" [nzXs]="24">
                <input
                  nz-input
                  id="companyName"
                  formControlName="companyName"
                />
                <fe-input-error-display
                  [control]="companyName"
                ></fe-input-error-display>
              </nz-form-control>
            </nz-form-item>

            <nz-divider
              nzText="Administrator OR Company Representative"
            ></nz-divider>
            <nz-form-item>
              <nz-form-label
                [nzSm]="8"
                [nzXs]="24"
                nzFor="firstName"
                [nzRequired]="true"
              >
                <span>First Name</span>
              </nz-form-label>
              <nz-form-control [nzSm]="14" [nzXs]="24">
                <input nz-input id="firstName" formControlName="firstName" />
                <fe-input-error-display
                  [control]="firstName"
                ></fe-input-error-display>
              </nz-form-control>
            </nz-form-item>

            <nz-form-item>
              <nz-form-label
                [nzSm]="8"
                [nzXs]="24"
                nzFor="surname"
                [nzRequired]="true"
              >
                <span>Surname</span>
              </nz-form-label>
              <nz-form-control [nzSm]="14" [nzXs]="24">
                <input nz-input id="surname" formControlName="surname" />
                <fe-input-error-display
                  [control]="surname"
                ></fe-input-error-display>
              </nz-form-control>
            </nz-form-item>

            <nz-form-item>
              <nz-form-label
                [nzSm]="8"
                [nzXs]="24"
                [nzRequired]="true"
                nzFor="email"
                >E-mail Address</nz-form-label
              >
              <nz-form-control [nzSm]="14" [nzXs]="24">
                <input nz-input formControlName="email" id="email" />
                <fe-input-error-display
                  [control]="email"
                ></fe-input-error-display>
              </nz-form-control>
            </nz-form-item>

            <nz-form-item>
              <nz-form-label
                [nzSm]="8"
                [nzXs]="24"
                nzFor="country"
                [nzRequired]="true"
                >Country</nz-form-label
              >
              <nz-form-control [nzSm]="14" [nzXs]="24">
                <nz-select
                  id="country"
                  formControlName="country"
                  nzShowSearch
                  nzPlaceHolder="Select operating country"
                  (ngModelChange)="onCountryChange($event)"
                >
                  <nz-option
                    *ngFor="let country of countries; trackBy: trackByCountryValue"
                    [nzValue]="country.value"
                    [nzLabel]="country.flag + ' ' + country.label">
                    {{ country.flag }} {{ country.label }}
                  </nz-option>
                </nz-select>
                <fe-input-error-display
                  [control]="country"
                ></fe-input-error-display>
              </nz-form-control>
            </nz-form-item>

            <nz-form-item>
              <nz-form-label
                [nzSm]="8"
                [nzXs]="24"
                nzFor="phoneNumber"
                [nzRequired]="true"
                >Phone Number</nz-form-label
              >
              <nz-form-control
                [nzSm]="14"
                [nzXs]="24"
                [nzValidateStatus]="form.controls['phoneNumber']"
              >
                <nz-input-group [nzAddOnBefore]="addOnBeforeTemplate">
                  <ng-template #addOnBeforeTemplate>
                    <div class="flex items-center gap-1 px-2 bg-gray-50 border-r min-w-[100px]">
                      <span class="text-sm">{{ selectedCountryFlag }}</span>
                      <span class="text-sm font-mono">{{ selectedCountryPhoneCode }}</span>
                    </div>
                  </ng-template>
                  <input
                    formControlName="phoneNumber"
                    digitOnly
                    autocomplete="off"
                    id="phoneNumber"
                    [placeholder]="getPhoneNumberPlaceholder()"
                    nz-input
                    (input)="onPhoneNumberInput($event)"
                    (blur)="onPhoneNumberBlur()"
                  />
                </nz-input-group>
                <div class="mt-1 text-xs text-gray-500" *ngIf="phoneNumberPreview">
                  Preview: {{ phoneNumberPreview }}
                </div>
                <fe-input-error-display
                  [control]="phoneNumber"
                ></fe-input-error-display>
              </nz-form-control>
            </nz-form-item>

          </form>
        </nz-spin>
      </ng-container>
    </nz-modal>
  `,
})
export class RegisterTenantModalComponent {
  private fb = inject(NonNullableFormBuilder);

  private companiesApi = inject(CompaniesApi);
  feMessageService = inject(FeFormMessagesService);
  logger = inject(NGXLogger);
  snackBar = inject(MatSnackBar);

  @Input() isVisible!: boolean;
  @Output() cancelled = new EventEmitter<boolean>();

  isSubmitting = false;

  countries = CountryUtils.getCountriesForDropdown();

  private readonly COUNTRY_STORAGE_KEY = 'admin-register-tenant-last-country';
  private readonly defaultCountry: string;

  selectedCountryPhoneCode: string;
  selectedCountryFlag: string;
  selectedCountryName: string;
  phoneNumberPreview = '';

  private readonly formErrorHandler = useFormErrorHandler();

  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  constructor() {
    // Initialize default country from localStorage
    this.defaultCountry = this.getDefaultCountry();

    // Initialize selected country properties
    this.selectedCountryPhoneCode = CountryUtils.getPhoneCodeByCountryName(this.defaultCountry);
    this.selectedCountryFlag = CountryUtils.getCountryByName(this.defaultCountry)?.flag || '🇺🇬';
    this.selectedCountryName = this.defaultCountry;

    // Update form control with default country
    this.form.get('country')?.setValue(this.defaultCountry, { emitEvent: false });

    this.updatePhoneNumberPreview();
  }

  form: FormGroup<{
    companyName: FormControl<string>;
    firstName: FormControl<string>;
    surname: FormControl<string>;
    email: FormControl<string>;
    phoneNumber: FormControl<string>;
    country: FormControl<string>;
  }> = this.fb.group({
    companyName: ['', [Validators.required]],
    firstName: ['', [Validators.required]],
    surname: ['', [Validators.required]],
    email: ['', [Validators.email, Validators.required]],
    phoneNumber: ['', [Validators.required, this.phoneNumberValidator.bind(this)]],
    country: ['Uganda', [Validators.required]], // Will be updated in constructor
  });

  companyName = this.form.get('companyName');
  firstName = this.form.get('firstName');
  surname = this.form.get('surname');
  email = this.form.get('email');
  phoneNumber = this.form.get('phoneNumber');
  country = this.form.get('country');

  mutation = injectMutation(() => ({
    mutationFn: (payload: RegisterNewTenantDto) =>
      lastValueFrom(this.companiesApi.companyControllerRegisterCompany({ body: payload })),
    onSuccess: () => {
      this.snackBar.open('New business registered successfully!', 'Close', {
        duration: 3000,
      });
      this.cancelled.emit(true);
      this.isSubmitting = false;
    },
    onError: (error: ApiError) => {
      this.logger.error('Error registering new tenant', error);

      const result = this.formErrorHandler.handleFormError(error, { formName: 'register-tenant-form' });

      this.showError.set(true);
      this.errorDetails = result.problemDetail;

      this.isSubmitting = false;
    },
  }));

  phoneNumberValidator(control: FormControl): { [key: string]: any } | null {
    if (!control.value || !this.selectedCountryName) {
      return null;
    }

    const result = PhoneNumberUtils.validateAndFormat(control.value, this.selectedCountryName);
    if (!result.isValid) {
      return { phoneNumber: { message: result.error } };
    }

    return null;
  }

  getPhoneNumberPlaceholder(): string {
    return PhoneNumberUtils.getPhonePlaceholder(this.selectedCountryName);
  }

  onPhoneNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Update preview as user types
    this.updatePhoneNumberPreview(value);
  }

  onPhoneNumberBlur(): void {
    const phoneValue = this.form.get('phoneNumber')?.value;
    if (phoneValue && this.selectedCountryName) {
      const result = PhoneNumberUtils.validateAndFormat(phoneValue, this.selectedCountryName);
      if (result.isValid) {
        // Set the formatted local version in the form
        this.form.get('phoneNumber')?.setValue(result.formatted, { emitEvent: false });
        this.updatePhoneNumberPreview(result.formatted);
      }
    }
  }

  updatePhoneNumberPreview(phoneValue?: string): void {
    const value = phoneValue || this.form.get('phoneNumber')?.value || '';
    if (value && this.selectedCountryName) {
      const result = PhoneNumberUtils.validateAndFormat(value, this.selectedCountryName);
      this.phoneNumberPreview = result.isValid ? result.display : '';
    } else {
      this.phoneNumberPreview = '';
    }
  }

  clearFormErrors() {
    this.feMessageService.emitError([]);

    this.showError.set(false);
    this.errorDetails = null;
  }
  submitForm(): void {
    if (this.isSubmitting) return;

    this.clearFormErrors();

    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });

      this.feMessageService.emitError('Check and provide valid and required information');
      return;
    }

    const countryValue = this.form.get('country')!.value;

    // Ensure country is properly set - use selectedCountryName as fallback
    const finalCountry = countryValue || this.selectedCountryName || 'Uganda';

    const payload: RegisterNewTenantDto = {
      companyName: this.form.get('companyName')!.value!,
      firstName: this.form.get('firstName')!.value!,
      surname: this.form.get('surname')!.value!,
      email: this.form.get('email')!.value,
      phoneNumber: this.form.get('phoneNumber')!.value,
      country: finalCountry as 'Uganda' | 'Ghana' | undefined,
    };

    this.isSubmitting = true;
    this.mutation.mutate(payload);
  }

  onCountryChange(selectedCountry: string): void {
    // Ensure form control is updated
    this.form.get('country')?.setValue(selectedCountry, { emitEvent: false });

    this.selectedCountryPhoneCode = CountryUtils.getPhoneCodeByCountryName(selectedCountry);
    this.selectedCountryFlag = CountryUtils.getCountryByName(selectedCountry)?.flag || '🇺🇬';
    this.selectedCountryName = selectedCountry;

    // Save selected country to localStorage
    this.saveLastSelectedCountry(selectedCountry);

    // Re-validate phone number when country changes
    this.form.get('phoneNumber')?.updateValueAndValidity();
    this.updatePhoneNumberPreview();
  }

  /**
   * Get default country from localStorage or fallback to 'Uganda'
   */
  private getDefaultCountry(): string {
    try {
      const savedCountry = localStorage.getItem(this.COUNTRY_STORAGE_KEY);
      if (savedCountry) {
        // Validate that the saved country exists in our countries list
        const countryExists = this.countries.some((c) => c.value === savedCountry);
        if (countryExists) {
          return savedCountry;
        }
      }
    } catch (error) {
      this.logger.warn('Failed to read country from localStorage:', error);
    }
    return 'Uganda';
  }

  /**
   * Save selected country to localStorage
   */
  private saveLastSelectedCountry(country: string): void {
    try {
      localStorage.setItem(this.COUNTRY_STORAGE_KEY, country);
    } catch (error) {
      this.logger.warn('Failed to save country to localStorage:', error);
    }
  }

  trackByCountryValue(
    _index: number,
    country: { label: string; value: string; phoneCode?: string; flag?: string },
  ): string {
    return country.value;
  }
}
